/**
 * Types de contrats de financement islamique conformes à la Sharia
 * Islamic Financing Contract Types - Sharia Compliant
 */

export enum ContractCategory {
  MURABAHA = 'MURABAHA',
  MUDARABA = 'MUDARABA',
  MUSHARAKAH = 'MUSHARAKAH',
  IJARA = 'IJARA',
  ISTISNA = 'ISTISNA',
  SALAM = 'SALAM',
  QUARD_AL_HASAN = 'QUARD_AL_HASAN',
  TAWARRUQ = 'TAWARRUQ',
  WAKALA = 'WAKALA',
  SUKUK = 'SUKUK'
}

export interface ContractTypeInfo {
  code: ContractCategory;
  name: string;
  nameAr: string;
  description: string;
  shariaCompliance: string;
  keyFeatures: string[];
  suitableFor: string[];
}

export const CONTRACT_TYPES: Record<ContractCategory, ContractTypeInfo> = {
  [ContractCategory.MURABAHA]: {
    code: ContractCategory.MURABAHA,
    name: 'Murabaha',
    nameAr: 'مرابحة',
    description: 'Cost-plus financing where the bank purchases an asset and sells it to the client at a markup',
    shariaCompliance: 'Compliant - No interest, transparent pricing, asset-based',
    keyFeatures: [
      'Bank buys asset first',
      'Known markup price',
      'Deferred payment allowed',
      'Asset ownership transfer'
    ],
    suitableFor: [
      'Equipment purchase',
      'Vehicle financing',
      'Real estate',
      'Consumer goods'
    ]
  },
  [ContractCategory.MUDARABA]: {
    code: ContractCategory.MUDARABA,
    name: 'Mudaraba',
    nameAr: 'مضاربة',
    description: 'Profit-sharing partnership where one party provides capital and the other provides expertise',
    shariaCompliance: 'Compliant - Risk-sharing, profit-sharing based on agreement',
    keyFeatures: [
      'Capital provider (Rabb-ul-Mal)',
      'Entrepreneur (Mudarib)',
      'Profit sharing ratio',
      'Loss borne by capital provider'
    ],
    suitableFor: [
      'Business startups',
      'Investment projects',
      'Working capital',
      'Trade financing'
    ]
  },
  [ContractCategory.MUSHARAKAH]: {
    code: ContractCategory.MUSHARAKAH,
    name: 'Musharakah',
    nameAr: 'مشاركة',
    description: 'Joint venture partnership where all partners contribute capital and share profits/losses',
    shariaCompliance: 'Compliant - Equal risk and profit sharing',
    keyFeatures: [
      'Joint capital contribution',
      'Shared management',
      'Proportional profit/loss',
      'Gradual buyout possible'
    ],
    suitableFor: [
      'Large projects',
      'Real estate development',
      'Business expansion',
      'Infrastructure'
    ]
  },
  [ContractCategory.IJARA]: {
    code: ContractCategory.IJARA,
    name: 'Ijara',
    nameAr: 'إجارة',
    description: 'Leasing arrangement where the bank owns the asset and leases it to the client',
    shariaCompliance: 'Compliant - Asset ownership retained, rental payments',
    keyFeatures: [
      'Bank retains ownership',
      'Periodic rental payments',
      'Maintenance responsibility',
      'Purchase option (Ijara Wa Iqtina)'
    ],
    suitableFor: [
      'Vehicle leasing',
      'Equipment leasing',
      'Property leasing',
      'Machinery'
    ]
  },
  [ContractCategory.ISTISNA]: {
    code: ContractCategory.ISTISNA,
    name: 'Istisna',
    nameAr: 'استصناع',
    description: 'Construction/Manufacturing financing where asset is built to specifications',
    shariaCompliance: 'Compliant - Future delivery, progress payments',
    keyFeatures: [
      'Custom manufacturing',
      'Progress payments',
      'Quality specifications',
      'Delivery timeline'
    ],
    suitableFor: [
      'Construction projects',
      'Manufacturing',
      'Infrastructure',
      'Custom equipment'
    ]
  },
  [ContractCategory.SALAM]: {
    code: ContractCategory.SALAM,
    name: 'Salam',
    nameAr: 'سلم',
    description: 'Advance payment for future delivery of goods',
    shariaCompliance: 'Compliant - Agricultural financing, price paid in advance',
    keyFeatures: [
      'Full advance payment',
      'Future delivery',
      'Commodity-based',
      'Price fixed upfront'
    ],
    suitableFor: [
      'Agricultural financing',
      'Commodity trading',
      'Seasonal crops',
      'Raw materials'
    ]
  },
  [ContractCategory.QUARD_AL_HASAN]: {
    code: ContractCategory.QUARD_AL_HASAN,
    name: 'Quard al-Hasan',
    nameAr: 'قرض حسن',
    description: 'Benevolent loan with no interest - purely for charitable or social purposes',
    shariaCompliance: 'Compliant - No interest, charitable purpose',
    keyFeatures: [
      'No interest charged',
      'Repayment of principal only',
      'Charitable/social purpose',
      'Optional voluntary contribution'
    ],
    suitableFor: [
      'Social welfare',
      'Emergency assistance',
      'Student loans',
      'Microfinance'
    ]
  },
  [ContractCategory.TAWARRUQ]: {
    code: ContractCategory.TAWARRUQ,
    name: 'Tawarruq',
    nameAr: 'تورق',
    description: 'Cash financing through commodity purchase and immediate resale',
    shariaCompliance: 'Compliant when properly structured - Triple sale contract',
    keyFeatures: [
      'Commodity purchase',
      'Immediate resale',
      'Cash liquidity',
      'Sharia board approval required'
    ],
    suitableFor: [
      'Liquidity needs',
      'Working capital',
      'Short-term financing'
    ]
  },
  [ContractCategory.WAKALA]: {
    code: ContractCategory.WAKALA,
    name: 'Wakala',
    nameAr: 'وكالة',
    description: 'Agency arrangement where one party acts on behalf of another for a fee',
    shariaCompliance: 'Compliant - Agency fee, no interest',
    keyFeatures: [
      'Agency relationship',
      'Service fee',
      'Fiduciary duty',
      'Clear mandate'
    ],
    suitableFor: [
      'Investment management',
      'Fund management',
      'Agency services',
      'Asset management'
    ]
  },
  [ContractCategory.SUKUK]: {
    code: ContractCategory.SUKUK,
    name: 'Sukuk',
    nameAr: 'صكوك',
    description: 'Islamic bonds representing ownership in an asset or project',
    shariaCompliance: 'Compliant - Asset-backed, no interest, profit sharing',
    keyFeatures: [
      'Asset-backed securities',
      'Regular returns',
      'Tradable',
      'Sharia-compliant structure'
    ],
    suitableFor: [
      'Large-scale projects',
      'Government financing',
      'Corporate bonds',
      'Infrastructure'
    ]
  }
};

export const getContractTypeByCode = (code: string): ContractTypeInfo | undefined => {
  return CONTRACT_TYPES[code as ContractCategory];
};

export const getContractTypesByCategory = (category: string): ContractTypeInfo[] => {
  return Object.values(CONTRACT_TYPES).filter(
    type => type.code === category
  );
};

export const getAllContractTypes = (): ContractTypeInfo[] => {
  return Object.values(CONTRACT_TYPES);
};
